import { FC, useState, useEffect, useMemo, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';

import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { useAppDispatch } from '../../../hooks/redux';

import { useGetCloudsHelperQuestionsQuery } from '../../../services/directus.api';
import { useUploadCloudsImageMutation } from '../../../services/clouds.api';
import { ConfirmDialog, CloseButton, Spinner, Heading, ArrowButton, Pressable, Button } from '../../../shared/ui';
import { IJournalResult } from '../../../store/types';
import { useFeatureFlagEnabled } from '../../../hooks/useFeatureFlagEnabled';
import { FEATURE_FLAG_TIPS } from '../../../constants/featureFlags';

import { useMobileToolSheet } from '../../meet/tools-panel/mobile-tool-sheet';

import { CloudsViewer, exportCloudsImageToBlob } from './CloudsViewer';
import { Line } from './types';

// Хук для определения высоты viewport
const useViewportHeight = () => {
  const [height, setHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 0);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return height;
};

interface ICloudsPsyProps {
  roomName?: string;
  consultationId?: number;
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  onToolActivated?: (tool: string) => void;
  sendCloudsOpen?: () => void;
  sendCloudsClose?: () => void;
  clientLines?: Line[];
  clientSelectedImage?: number | null;
  clientFinished?: boolean;
  onFinishTool?: () => void;
  onCloseTool?: () => void;
  onSaveClouds?: () => Promise<void>;
  /** Мобильный 1:1: панель в нижней шторке поверх видео; рисунок клиента и подсказки — тоже в шторке */
  mobile?: boolean;
}

const MAX_CLOUD_ID = 100;

// Компонент для отслеживания ошибок загрузки изображений
const CloudImageItem: FC<{
  imageId: number;
  index: number;
  onError: (imageId: number) => void;
  /** Высота превью, px (на телефоне меньше, чтобы сетка из 100 облаков не была бесконечной) */
  heightPx?: number;
}> = ({ imageId, index, onError, heightPx = 180 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Intersection Observer для lazy loading
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
          }
        });
      },
      { rootMargin: '100px' },
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const smallUrl = `https://storage.yandexcloud.net/zingizi/clouds/small/${imageId}.jpg`;

  return (
    <div ref={containerRef} className="relative rounded-control overflow-hidden" style={{ height: `${heightPx}px` }}>
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner className="text-ink-subtle" />
        </div>
      )}
      {inView && (
        <img
          ref={imgRef}
          src={smallUrl}
          alt={`Облако ${index + 1}`}
          className="w-full h-full object-cover transition-opacity duration-300"
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => {
            // Если изображение не загрузилось, скрываем элемент
            onError(imageId);
          }}
          style={{ opacity: loaded ? 1 : 0 }}
        />
      )}
    </div>
  );
};

export const CloudsPsy: FC<ICloudsPsyProps> = ({
  roomName,
  consultationId,
  result,
  setResult,
  onToolActivated,
  sendCloudsOpen,
  sendCloudsClose,
  clientLines = [],
  clientSelectedImage = null,
  clientFinished = false,
  onFinishTool,
  onCloseTool,
  onSaveClouds,
  mobile = false,
}) => {
  const sheet = useMobileToolSheet();
  const dispatch = useAppDispatch();
  const viewportHeight = useViewportHeight();
  const [active, setActive] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isSliderVisible, setIsSliderVisible] = useState(true);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const isTipsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TIPS);
  const [uploadCloudsImage] = useUploadCloudsImageMutation();
  const savedImageRef = useRef<string | null>(null);
  const previousClientFinishedRef = useRef<boolean>(clientFinished);
  // Создаем список всех ID изображений (1-100) без загрузки
  const cloudImageIds = useMemo(() => Array.from({ length: MAX_CLOUD_ID }, (_, i) => i + 1), []);

  // Отслеживаем изображения с ошибками загрузки, чтобы не показывать их
  const [failedImageIds, setFailedImageIds] = useState<Set<number>>(new Set());

  // Фильтруем список, исключая изображения с ошибками
  const validCloudImageIds = useMemo(
    () => cloudImageIds.filter((id) => !failedImageIds.has(id)),
    [cloudImageIds, failedImageIds],
  );

  const containerPadding = viewportHeight < 900 ? 'p-2 mt-2' : 'p-8';

  const fontSize = viewportHeight < 900 ? '16px' : '24px';

  const marginBottom = viewportHeight < 900 ? 'mb-1' : 'mb-4';
  const marginBottomParagraph = viewportHeight < 900 ? 'mb-1' : 'mb-2';

  // Используем imageId напрямую для получения URL
  const selectedCloudImageId =
    clientSelectedImage !== null && clientSelectedImage >= 0 && clientSelectedImage < cloudImageIds.length
      ? cloudImageIds[clientSelectedImage]
      : null;

  const selectedCloudBackgroundUrl = selectedCloudImageId
    ? `https://storage.yandexcloud.net/zingizi/clouds/${selectedCloudImageId}.jpg`
    : '';

  // Запрос отправляется когда клиент завершил рисунок
  const { data: helperQuestions } = useGetCloudsHelperQuestionsQuery(undefined, {
    skip: !clientFinished || !isTipsEnabled,
  });

  // Используем моковые данные пока не загрузились вопросы из API
  const questions = helperQuestions?.data?.map((q) => q.formatted_text) || [];

  const handleStartSession = () => {
    // Отправляем через data channel livekit
    sendCloudsOpen?.();
    dispatch(setActiveTool(PsyToolsId.CLOUDS));
    onToolActivated?.('clouds');
    setActive(true);
    // Мобильный: убираем шторку, чтобы видеть клиента, пока он выбирает облако
    sheet?.collapse();
  };

  // Мобильный: клиент выбрал облако — раскрываем шторку с его рисунком
  useEffect(() => {
    if (clientSelectedImage !== null) {
      sheet?.expand();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на выбор облака
  }, [clientSelectedImage]);

  const handleFinishTool = () => {
    setActive(false);
    dispatch(setActiveTool(0));
    onFinishTool?.();
  };

  const handleRequestClose = () => {
    setIsConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setIsConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setIsConfirmCloseOpen(false);

    // Отправляем сообщение клиенту через data-channel для закрытия инструмента
    sendCloudsClose?.();
    handleFinishTool();

    // Закрываем инструмент в панели инструментов
    onCloseTool?.();
  };

  // Сброс слайдера когда клиент завершил рисунок
  useEffect(() => {
    if (clientFinished) {
      setIsSliderVisible(isTipsEnabled);
      setSliderIndex(0);
    }
  }, [clientFinished, isTipsEnabled]);

  useEffect(() => {
    // Обновляем ref для отслеживания предыдущего состояния
    const wasFinished = previousClientFinishedRef.current;
    previousClientFinishedRef.current = clientFinished;

    // Сохраняем результат в журнал только когда клиент завершил рисование и изображение еще не было сохранено
    // Важно: загрузка должна происходить только при переходе clientFinished с false на true,
    // а не при перезаходе на инструмент, когда clientFinished уже true
    if (
      clientFinished &&
      !wasFinished && // Ключевая проверка: загрузка только при переходе с false на true
      clientSelectedImage !== null &&
      clientLines.length > 0 &&
      result &&
      setResult &&
      consultationId
    ) {
      const cloudImageId = cloudImageIds[clientSelectedImage];
      const saveKey = `${clientSelectedImage}-${clientLines.length}`;

      // Проверяем, что это изображение еще не было сохранено
      if (savedImageRef.current === saveKey) {
        return;
      }

      // Дополнительная проверка: не загружать, если такое изображение уже есть в журнале
      const alreadySaved = result.clouds?.some(
        (entry) => entry.imageId === cloudImageId && entry.selected_image_index === clientSelectedImage && entry.image, // Проверяем, что файл уже был загружен
      );

      if (alreadySaved) {
        // Помечаем как сохраненное, чтобы не пытаться загрузить снова
        savedImageRef.current = saveKey;
        return;
      }

      if (!cloudImageId) {
        console.warn('Cloud image ID is not available for selected index', clientSelectedImage);
      } else {
        const saveCloudsImage = async () => {
          try {
            // Помечаем как сохраняемое, чтобы избежать повторных вызовов
            savedImageRef.current = saveKey;

            const imageUrl = `https://storage.yandexcloud.net/zingizi/clouds/${cloudImageId}.jpg`;

            const loadedImage = await new Promise<HTMLImageElement>((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'anonymous';

              const timeout = setTimeout(() => {
                reject(new Error('Таймаут загрузки изображения'));
              }, 10000);

              img.onload = () => {
                clearTimeout(timeout);
                if (img.width === 0 || img.height === 0) {
                  reject(new Error('Изображение имеет нулевой размер'));
                } else {
                  resolve(img);
                }
              };

              img.onerror = () => {
                clearTimeout(timeout);
                reject(new Error('Не удалось загрузить изображение'));
              };

              img.src = imageUrl;
            });

            // Экспортируем изображение в Blob
            const imageBlob = await exportCloudsImageToBlob(loadedImage, clientLines, 800, 600);

            // Генерируем UUID для имени файла
            const uuid = crypto.randomUUID();
            const filename = `${uuid}.png`;

            // Загружаем файл через API
            const uploadResult = await uploadCloudsImage({
              consultationId: consultationId.toString(),
              filename,
              file: imageBlob,
            }).unwrap();

            // Сохраняем имя файла (UUID с расширением) в поле image
            const cloudsEntry = {
              date_time: new Date().toISOString(),
              image: filename,
              imageId: cloudImageId,
              selected_image_index: clientSelectedImage,
              comment: '',
            };

            setResult((prevResult) => ({
              ...prevResult,
              clouds: [...(prevResult.clouds || []), cloudsEntry],
            }));
          } catch (error) {
            // В случае ошибки сбрасываем флаг, чтобы можно было повторить попытку
            savedImageRef.current = null;
            console.error('Failed to upload clouds image:', error);
          }
        };

        void saveCloudsImage();
      }
    }
  }, [
    clientFinished,
    clientSelectedImage,
    clientLines.length,
    setResult,
    consultationId,
    cloudImageIds,
    uploadCloudsImage,
  ]);

  const tipsInner = (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto pr-2">
        <div
          className="leading-relaxed break-words min-w-0 pl-2"
          style={{ fontSize, wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          <p className={classNames('font-medium', marginBottom)} style={{ fontSize }}>
            {helperQuestions?.data?.[sliderIndex]?.title}
          </p>

          <ReactMarkdown
            components={{
              p: (paragraphProps) => (
                <p
                  className={classNames('mt-0 last:mb-0 break-words', marginBottomParagraph)}
                  style={{ fontSize, wordBreak: 'break-word', overflowWrap: 'break-word' }}
                  {...paragraphProps}
                />
              ),
              ol: (olProps) => (
                <ol className="list-decimal pl-8" style={{ fontSize, listStylePosition: 'outside' }} {...olProps} />
              ),
              li: (liProps) => (
                <li
                  className="break-words"
                  style={{
                    fontSize,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    display: 'list-item',
                  }}
                  {...liProps}
                />
              ),
            }}
          >
            {questions[sliderIndex]}
          </ReactMarkdown>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-6 flex-shrink-0">
        <ArrowButton
          direction="prev"
          label="Предыдущий вопрос"
          disabled={sliderIndex === 0}
          onClick={() => setSliderIndex((prev) => Math.max(0, prev - 1))}
        />

        <div className="flex items-center gap-6">
          {questions.map((_, idx) => {
            const isActive = idx === sliderIndex;

            return (
              <Pressable
                key={`question-dot-${idx}`}
                onClick={() => setSliderIndex(idx)}
                aria-label={`Перейти к вопросу ${idx + 1}`}
                className={classNames(
                  'rounded-full transition-colors',
                  isActive ? 'bg-ink-subtle' : 'bg-line hover:bg-line-strong',
                )}
                style={{ width: isActive ? 20 : 14, height: isActive ? 20 : 14 }}
              />
            );
          })}
        </div>

        <ArrowButton
          direction="next"
          label="Следующий вопрос"
          disabled={sliderIndex === questions.length - 1}
          onClick={() => setSliderIndex((prev) => Math.min(questions.length - 1, prev + 1))}
        />
      </div>
    </div>
  );

  if (mobile) {
    const showDrawing = active && clientSelectedImage !== null;

    return (
      <div className="flex w-full flex-col gap-3 text-white">
        {!showDrawing ? (
          <>
            <p className="text-ui leading-relaxed text-white/90">
              Предложите клиенту рассмотреть 50 изображений облаков и выбрать то, которое наиболее откликается ему в
              текущий момент. После выбора клиент сможет с помощью кисти обвести тот образ, который он видит в
              очертаниях облака. Используйте получившийся рисунок как проекцию для дальнейшей беседы.
            </p>
            <div className="grid grid-cols-3 gap-2">
              {validCloudImageIds.map((imageId, index) => (
                <CloudImageItem
                  key={imageId}
                  imageId={imageId}
                  index={index}
                  heightPx={96}
                  onError={(id) => setFailedImageIds((prev) => new Set(prev).add(id))}
                />
              ))}
            </div>
            <div className="sticky bottom-[-1rem] z-40 -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              <Button size="lg" fullWidth loading={active} onClick={handleStartSession}>
                {active ? 'Клиент думает…' : 'Начать сессию с облаками'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="aspect-[4/3] w-full overflow-hidden rounded-control bg-white">
              {selectedCloudBackgroundUrl ? (
                <CloudsViewer backgroundImage={selectedCloudBackgroundUrl} lines={clientLines} />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Spinner className="text-ink-subtle" />
                </div>
              )}
            </div>

            {clientFinished && isSliderVisible && isTipsEnabled && (
              <div className="relative flex min-h-[200px] flex-col rounded-card border border-accent bg-white p-4 text-ink">
                {tipsInner}
              </div>
            )}

            <div className="sticky bottom-[-1rem] z-40 -mx-4 flex gap-2 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              {clientFinished ? (
                <>
                  {isTipsEnabled && (
                    <Button
                      size="md"
                      variant="ghostInverse"
                      fullWidth
                      onClick={() => setIsSliderVisible((visible) => !visible)}
                    >
                      {isSliderVisible ? 'Скрыть подсказки' : 'Открыть подсказки'}
                    </Button>
                  )}
                  <Button size="md" fullWidth onClick={handleRequestClose}>
                    Завершить
                  </Button>
                </>
              ) : (
                <Button size="lg" fullWidth loading>
                  Клиент рисует…
                </Button>
              )}
            </div>
          </>
        )}

        <ConfirmDialog
          isOpen={isConfirmCloseOpen}
          title="Закрытие инструмента"
          message="Вы действительно хотите закрыть инструмент «Облака»? Клиент перестанет видеть облака."
          cancelText="Оставить открытым"
          confirmText="Закрыть инструмент"
          onCancel={handleCancelClose}
          onConfirm={handleConfirmClose}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {(!active || (active && clientSelectedImage === null)) && (
        <div className="w-[500px] min-w-[500px] h-full flex flex-col text-white rounded-l-control pt-6 px-6 relative">
          <p className="text-display-s mb-4">Облака</p>
          <div className="text-body-l leading-relaxed mb-6">
            Предложите клиенту рассмотреть 50 изображений облаков и выбрать то, которое наиболее откликается ему в
            текущий момент.
            <br />
            <br />
            После выбора клиент сможет с помощью кисти обвести тот образ, который он видит в очертаниях облака.
            <br />
            <br />
            Используйте получившийся рисунок как проекцию для дальнейшей беседы.
          </div>

          <div className="flex-1 min-h-0">
            <div className="h-full overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3 pb-[100px]">
                {validCloudImageIds.map((imageId, index) => (
                  <CloudImageItem
                    key={imageId}
                    imageId={imageId}
                    index={index}
                    onError={(id) => {
                      // Если изображение не загрузилось, добавляем его в список с ошибками
                      setFailedImageIds((prev) => new Set(prev).add(id));
                    }}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="absolute bottom-[24px] left-6 right-8 flex justify-start pointer-events-none">
            <Button size="lg" fullWidth className="pointer-events-auto" loading={active} onClick={handleStartSession}>
              {active ? 'Клиент думает…' : 'Начать сессию с облаками'}
            </Button>
          </div>
        </div>
      )}

      {active && clientSelectedImage !== null && (
        <div className="w-[830px] min-w-[830px] h-full flex flex-col text-white p-6 relative">
          <Heading tone="inverse" size="lg" className="mb-6">
            Облака
          </Heading>

          {/* Показываем статус когда клиент рисует */}

          <div className="flex flex-col gap-6 flex-1 min-h-0 overflow-hidden">
            {/* Картинка - всегда видна, размер зависит от состояния подсказок */}
            <div
              className="bg-white rounded-control overflow-hidden flex-shrink-0"
              style={
                isSliderVisible && clientFinished
                  ? { width: '500px', aspectRatio: '4/3' }
                  : { width: '782px', aspectRatio: '16/9' }
              }
            >
              {selectedCloudBackgroundUrl ? (
                <CloudsViewer backgroundImage={selectedCloudBackgroundUrl} lines={clientLines} />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Spinner className="text-ink-subtle" />
                </div>
              )}
            </div>

            {/* Слайдер с вопросами (появляется после завершения) */}
            {clientFinished && isSliderVisible && isTipsEnabled && (
              <>
                <div
                  className={classNames(
                    'bg-white border-1 border-accent text-ink rounded-card flex flex-col min-h-0 overflow-hidden flex-shrink-0 relative',
                    containerPadding,
                  )}
                  style={{ flex: '1.2 1 50%', minHeight: 0 }}
                >
                  <CloseButton
                    label="Закрыть подсказки"
                    className="absolute top-2 right-5 -m-1.5"
                    onClick={() => setIsSliderVisible(false)}
                  />
                  {tipsInner}
                </div>
              </>
            )}

            {/* Кнопки когда слайдер скрыт или клиент еще рисует */}
            {(!clientFinished || !isSliderVisible) && (
              <div className="absolute bottom-6 left-6 right-6 flex gap-3 flex-shrink-0">
                {clientFinished && !isSliderVisible && isTipsEnabled && (
                  <Button size="lg" variant="neutral" className="flex-1" onClick={() => setIsSliderVisible(true)}>
                    Открыть подсказки
                  </Button>
                )}
                {clientFinished ? (
                  <Button size="lg" className="flex-1" onClick={handleRequestClose}>
                    Завершить работу с инструментом
                  </Button>
                ) : (
                  <Button size="lg" fullWidth loading>
                    Клиент рисует…
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={isConfirmCloseOpen}
        title="Закрытие инструмента"
        message="Вы действительно хотите закрыть инструмент «Облака»? Клиент перестанет видеть облака."
        cancelText="Оставить открытым"
        confirmText="Закрыть инструмент"
        onCancel={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </div>
  );
};
